// La question d'une séance de QCM : l'énoncé, les propositions, l'action « Valider », puis la correction qui
// s'ouvre dessous. Trois nœuds seulement changent entre « je réponds » et « je suis corrigé » (#qcm-props,
// #qcm-actions, #qcm-correction) : l'énoncé, ses images et l'en-tête ne sont jamais redessinés.
//
// Tous les types de la banque edn.fr sont rendus ici : réponse unique, réponses multiples (barème EDN),
// concordance de script (TCS, points partiels), réponse à saisir, et zone à pointer sur une image.
import { html, attr, brut } from '../../core/html.js';
import { state } from '../../core/store.js';
import { ico } from '../icones.js';
import { puceRang, puceScore } from './qcm-jauge.js';

// L'item dont relève une question : celui de la séance, ou — pour une annale, qui mêle plusieurs items — celui de
// la question elle-même (sa notion). C'est lui qui porte la maîtrise et vers lui que mènent les liens de notion.
function itemDe(s, q) { return s && s.annale ? (q && q.item_numero != null ? q.item_numero : '') : s.n; }

function Q() { return window.qcmInterne || {}; }
function pct(x) { return Math.round((x || 0) * 100); }
function esc(s) { return window.escapeHtml(s == null ? '' : String(s)); }
function riche(h) { return brut(Q().cleanHtml ? Q().cleanHtml(h) : esc(h)); }
function texteEnLignes(t) { return brut(esc(t).replace(/\n/g, '<br>')); }
function pluriel(n, un, des) { return n + ' ' + (n > 1 ? des : un); }

// ---------------------------------------------------------------- propositions
// Une proposition est une ligne à cocher de 44 px. Avant la validation, le cobalt marque le choix ; après, chaque
// ligne porte SON MOT (attendue / cochée à tort / manquée) en plus de sa teinte : la couleur ne juge pas seule.
function verdictProposition(q, p, choisie, tcs) {
    if (tcs) {
        var c = p.coefficient == null ? (p.est_correcte ? 1 : 0) : p.coefficient;
        return { etat: c >= 1 ? 'juste' : c > 0 ? 'partiel' : (choisie ? 'faux' : 'neutre'),
            mot: (Math.round(c * 100) / 100).toString().replace('.', ',') + ' pt', aide: 'Points rapportés par cet échelon' };
    }
    // Indispensable et inacceptable se disent toujours, cochées ou non : l'une doit être cochée, l'autre jamais.
    if (p.indispensable) return choisie
        ? { etat: 'juste', mot: 'indispensable', aide: 'Réponse indispensable : tu l\'avais cochée' }
        : { etat: 'manquee', mot: 'indispensable oubliée', aide: 'Réponse indispensable : tu ne l\'avais pas cochée' };
    if (p.inacceptable) return choisie
        ? { etat: 'faux', mot: 'inacceptable', aide: 'Réponse inacceptable : tu l\'avais cochée' }
        : { etat: 'neutre', mot: 'inacceptable', aide: 'Réponse inacceptable : tu as bien fait de ne pas la cocher' };
    if (p.est_correcte && choisie) return { etat: 'juste', mot: 'attendue', aide: 'Bonne réponse, tu l\'avais cochée' };
    if (p.est_correcte && !choisie) return { etat: 'manquee', mot: 'manquée', aide: 'Bonne réponse, tu ne l\'avais pas cochée' };
    if (!p.est_correcte && choisie) return { etat: 'faux', mot: 'cochée à tort', aide: 'Cette proposition est fausse' };
    return { etat: 'neutre', mot: '', aide: '' };
}

// Tant qu'on répond, la proposition est une vraie case à cocher (ou un vrai bouton radio) dans son étiquette :
// le clavier, le groupement et l'annonce viennent du navigateur, pas d'un rôle ARIA recopié à la main. Le carré
// dessiné est décoratif ; la case native est là, simplement masquée à l'œil.
function proposition(q, p, s) {
    var i = Q(), multiple = i.isMany(q), tcs = q.methode_reponse === 'TCS';
    var choisie = multiple ? !!(s.ans && s.ans[p.id]) : s.ans === p.id;
    var v = s.revealed ? verdictProposition(q, p, choisie, tcs) : null;
    var stat = (s.revealed && p.pourcentage_cochage != null)
        ? html`<span class="qcm-prop-stat num" title="Part des étudiants d'edn.fr ayant coché cette proposition">${Math.round(p.pourcentage_cochage)} % cochent</span>` : '';
    var corps = html`<span class="qcm-prop-case" aria-hidden="true">${ico('check', { taille: 14 })}</span>
        <span class="qcm-prop-texte qcm-rich">${riche(p.texte_html || esc(p.texte))}</span>
        <span class="qcm-prop-fin">${stat}${v && v.mot ? html`<span class="qcm-prop-verdict" title="${v.aide}">${v.mot}</span>` : ''}</span>`;
    if (s.revealed) {
        return html`<div class="qcm-prop qcm-prop--${v.etat}${choisie ? ' qcm-prop--choisie' : ''}" data-prop="${p.id}">${corps}</div>`;
    }
    var bloquee = !choisie && i.isDefined && i.isDefined(q)
        && Object.keys(s.ans || {}).filter(function(k) { return s.ans[k]; }).length >= i.nbAttendu(q);
    return html`<label class="qcm-prop qcm-prop--vive${choisie ? ' qcm-prop--choisie' : ''}${bloquee ? ' qcm-prop--bloquee' : ''}" data-prop="${p.id}">
        <input class="qcm-prop-controle" type="${multiple ? 'checkbox' : 'radio'}" name="qcm-prop" value="${p.id}"${choisie ? html` checked` : ''}${bloquee ? html` disabled` : ''}
               data-change-action="${multiple ? 'qcmToggle' : 'qcmPick'}" data-args="${attr([p.id])}">
        ${corps}</label>`;
}

export function propositionsHtml(q, s) {
    var i = Q(), multiple = i.isMany(q);
    return html`<div id="qcm-props" class="qcm-props ${multiple ? 'qcm-props--multiple' : 'qcm-props--unique'}" role="${s.revealed ? 'list' : (multiple ? 'group' : 'radiogroup')}" aria-label="Propositions">
        ${i.propsForDisplay(q, s).map(function(p) { return proposition(q, p, s); })}
    </div>`;
}

// Une réponse est-elle prête à être validée ? (la zone demande un pointage, le reste non)
export function reponsePrete(q, s) {
    var i = Q();
    if (i.isArea(q)) return !!(s.ans && s.ans.x != null);
    if (i.isText(q) || i.isMany(q)) return true;
    return !!s.ans;
}

// ---------------------------------------------------------------- zone à pointer (MATCHING_AREA)
export function zoneCalque(q, s) {
    var i = Q(), rep = (s.ans && s.ans.x != null) ? s.ans : null, out = [];
    if (rep) out.push(html`<span class="qcm-zone-marque" style="left:${(rep.x / rep.nw * 100)}%; top:${(rep.y / rep.nh * 100)}%"></span>`);
    if (s.revealed) {
        var polys = i.correctPolys(q);
        var nw = (rep && rep.nw) || q._nw || 1000, nh = (rep && rep.nh) || q._nh || 1000;
        out.push(brut('<svg class="qcm-zone-svg" viewBox="0 0 ' + nw + ' ' + nh + '" preserveAspectRatio="none" aria-hidden="true">'
            + polys.map(function(poly) { return '<polygon points="' + poly.map(function(p) { return p[0] + ',' + p[1]; }).join(' ') + '"/>'; }).join('')
            + '</svg>'));
    }
    return html`${out}`;
}
export function zoneAide(s) {
    var pose = !!(s.ans && s.ans.x != null);
    return s.revealed ? 'La zone attendue est cernée de vert ; ton pointage est le disque cobalt.'
        : (pose ? 'Pointage posé : clique encore pour le déplacer.' : 'Clique sur l\'image pour poser ton pointage.');
}
function zoneHtml(q, s) {
    return html`<div class="qcm-zone">
        <p id="qcm-zone-aide" class="qcm-zone-legende">${zoneAide(s)}</p>
        <div class="qcm-zone-cadre">
            <img id="qcm-zone-img" class="qcm-zone-img" src="${Q().imgUrl(q.fichier)}" alt="Image de la question : clique dessus pour pointer la zone attendue"
                 data-qcm-img="${q.fichier || ''}" data-qcm-mesure="1"${s.revealed ? '' : html` data-action="qcmAreaClick" data-args="${attr(['qcm-zone-img'])}"`}>
            <span id="qcm-zone-calque" class="qcm-zone-calque" aria-hidden="true">${zoneCalque(q, s)}</span>
        </div>
    </div>`;
}

// ---------------------------------------------------------------- images et pièces jointes de l'énoncé
// Le contexte d'une image dit d'où EDN.fr l'a servie : « …problemStatementContents… » pour l'énoncé,
// « …correctionStatement… » pour la correction. Il commence souvent par « correction. » — le nom de la réponse
// d'API qui l'a portée, pas sa place : une annale arrive tout entière par sa correction. Seule la partie
// « correctionStatement » range donc une image dans la correction ; toutes les autres sont montrées avec la question.
// Une image sans contexte (ajoutée après coup, hors des réponses d'API) suit ses sœurs de la même question : si
// celles que le texte place sont toutes dans la correction, elle y va aussi ; sinon elle est montrée avec la question.
export function imageDeCorrection(im, q) {
    if (im && im.contexte) return /correctionStatement/i.test(im.contexte);
    var e = (q && q.enonce_html) || '', c = (q && q.correction_html) || '';
    var soeurs = ((q && q.images) || []).filter(function(x) { return x !== im && x.fichier; });
    return soeurs.some(function(x) { return c.indexOf(x.fichier) >= 0; }) && !soeurs.some(function(x) { return e.indexOf(x.fichier) >= 0; });
}
window.qcmImageDeCorrection = imageDeCorrection;
var IMAGE = /\.(png|jpe?g|gif|webp|svg|bmp|avif)$/i;
function images(q, quand) {
    var i = Q();
    var dedans = (q.enonce_html || '') + (q.correction_html || '');
    var fichierZone = String(q.fichier || '').split('/').pop();
    var liste = (q.images || []).filter(function(im) {
        var corr = imageDeCorrection(im, q);
        if (quand === 'correction' ? !corr : corr) return false;
        return dedans.indexOf(im.fichier) < 0 && im.fichier !== fichierZone;
    });
    if (!liste.length) return '';
    return html`<div class="qcm-images">${liste.map(function(im) {
        return html`<a class="qcm-image" href="${i.imgUrl(im.fichier)}" target="_blank" rel="noopener" title="Ouvrir l'image en grand">
            <img src="${i.imgUrl(im.fichier)}" alt="" data-qcm-img="${im.fichier}"></a>`;
    })}</div>`;
}
// Les pièces jointes qui sont des images déjà montrées par images() ne sont pas répétées en lien.
function piecesJointes(liste, q) {
    var montrees = (q.images || []).map(function(im) { return im.fichier; });
    var i = Q(), pj = (liste || []).filter(function(c) {
        var nom = String(c.fichier || '').split('/').pop();
        return c.fichier && !(IMAGE.test(nom) && montrees.indexOf(nom) >= 0);
    });
    if (!pj.length) return '';
    return html`<ul class="qcm-pj">${pj.map(function(c) {
        return html`<li><a href="${i.imgUrl(c.fichier)}" target="_blank" rel="noopener">${ico('file-text', { taille: 16 })}${c.titre || c.fichier}</a></li>`;
    })}</ul>`;
}

// ---------------------------------------------------------------- bandeau de la question
function bandeau(q, s) {
    var i = Q(), attendues = '';
    if (i.isMany(q) && q.nb_reponses_attendues) attendues = q.nb_reponses_attendues + ' réponse' + (q.nb_reponses_attendues > 1 ? 's' : '') + ' attendues';
    else if (/MANY_DEFINED/.test(q.methode_reponse || '')) { var m = /(\d+)\s+r[ée]ponse/i.exec(q.enonce || ''); if (m) attendues = m[1] + ' réponses attendues'; }
    var M = i.itemMastery(itemDe(s, q)), deja = M && M.qLast[q.id] !== undefined;
    return html`<div class="qcm-q-bandeau">
        <span class="puce puce-sans-point">${i.typeLabel(q)}</span>
        ${attendues ? html`<span class="puce puce-sans-point num">${attendues}</span>` : ''}
        ${q.est_pediatrique ? html`<span class="puce puce-pedia">Pédiatrie</span>` : ''}
        ${q.est_lisa ? html`<span class="puce puce-sans-point">LiSA</span>` : ''}
        ${(q.annales || []).length ? html`<span class="puce puce-sans-point">Annale</span>` : ''}
        ${q._ai ? html`<span class="puce puce-sans-point" title="Question rédigée par l'IA d'après la fiche et le collège">Écrite par l'IA</span>` : ''}
        ${q._override ? html`<span class="puce puce-accent" title="${q._override.note || 'Bonnes réponses ou texte corrigés depuis le site'}">Corrigée à la main</span>` : ''}
        ${q.taux_reussite_global != null ? html`<span class="qcm-q-national num" title="Taux de réussite des étudiants d'edn.fr">réussite nationale ${pct(q.taux_reussite_global)} %</span>` : ''}
        ${deja && !s.revealed ? html`<span class="qcm-q-national num" title="Ton dernier score sur cette question">déjà vue · ${pct(M.qLast[q.id])} %</span>` : ''}
    </div>`;
}

// ---------------------------------------------------------------- la zone de réponse (selon le type)
function zoneReponse(q, s) {
    var i = Q();
    if (i.isArea(q)) return zoneHtml(q, s);
    if (i.isText(q)) {
        return html`<div class="qcm-saisie champ">
            <label for="qcm-text-input">Ta réponse</label>
            <input id="qcm-text-input" class="saisie" type="text" autocomplete="off" spellcheck="false" enterkeyhint="done"
                   value="${typeof s.ans === 'string' ? s.ans : ''}" placeholder="Un mot, une valeur, une unité…"
                   data-input-action="qcmText" data-enter="qcmTextKey"${s.revealed ? html` disabled` : ''}>
        </div>`;
    }
    return html`${q.methode_reponse === 'TCS' ? html`<p class="qcm-q-consigne">Test de concordance de script : choisis l'effet de cette information sur ton hypothèse, de −2 à +2. L'échelon le plus choisi par le panel d'experts vaut 1 point ; les autres échelons choisis par une partie des experts en rapportent une part.</p>` : ''}
        ${propositionsHtml(q, s)}`;
}

// ---------------------------------------------------------------- « Valider », et la mention du barème
function baremeHtml(q) {
    var i = Q();
    if (i.isDefined && i.isDefined(q)) {
        var n = i.nbAttendu(q);
        return html`<p class="qcm-bareme num">${n} réponse${n > 1 ? 's' : ''} à cocher, pas une de plus · une part par bonne réponse : ${n > 1 ? '1/' + n + ' pt chacune' : '1 pt'}</p>`;
    }
    return html`<p class="qcm-bareme num">Barème EDN : 1 discordance = 0,5 pt · 2 = 0,2 pt · 3 et plus = 0</p>`;
}
export function actionsHtml(q, s) {
    var i = Q(), multiple = i.isMany(q), zone = i.isArea(q), pret = reponsePrete(q, s);
    if (s.revealed) return html`<div id="qcm-actions" class="qcm-q-actions" hidden></div>`;
    // Épreuve : la réponse est enregistrée sans correction ; celle-ci arrive d'un bloc à la fin.
    if (s.differe) {
        var derniere = s.idx + 1 >= s.list.length;
        return html`<div id="qcm-actions" class="qcm-q-actions">
            <button type="button" id="qcm-valider" class="btn btn-primaire" data-action="qcmValidate"${pret ? '' : html` disabled aria-disabled="true"`}>${derniere ? 'Terminer et voir la correction' : 'Question suivante'}${ico('arrow-right', { taille: 18 })}</button>
            ${zone || !pret ? html`<button type="button" class="btn btn-fantome" data-action="qcmValidate">Je ne sais pas, passer</button>` : ''}
            ${multiple ? baremeHtml(q) : ''}
        </div>`;
    }
    return html`<div id="qcm-actions" class="qcm-q-actions">
        <button type="button" id="qcm-valider" class="btn btn-primaire" data-action="qcmValidate"${pret ? '' : html` disabled aria-disabled="true"`}>Valider ma réponse</button>
        ${zone ? html`<button type="button" class="btn btn-fantome" data-action="qcmValidate">Je ne sais pas, voir la correction</button>` : ''}
        ${multiple ? baremeHtml(q) : ''}
    </div>`;
}

// ---------------------------------------------------------------- la correction
export function correctionHtml(q, s) {
    var i = Q(), r = s.results[s.results.length - 1];
    if (!s.revealed || !r) return html`<div id="qcm-correction" class="qcm-correction" hidden></div>`;
    var M = i.itemMastery(itemDe(s, q)), saisieOuZone = i.isText(q) || i.isArea(q);
    var partArg = s.parts || (s.part == null ? '' : s.part);   // séance réunie : on revient aux mêmes sous-parties
    return html`<div id="qcm-correction" class="qcm-correction" tabindex="-1" role="group" aria-label="Correction de la question">
        <div class="qcm-verdict" role="status">
            ${puceScore(r.score)}
            ${r.detail && r.detail.disc ? html`<span class="qcm-verdict-detail num">${r.detail.disc} discordance${r.detail.disc > 1 ? 's' : ''} avec le corrigé</span>` : ''}
            ${r.detail && r.detail.attendues ? html`<span class="qcm-verdict-detail num">${r.detail.trop ? 'Plus de ' + r.detail.attendues + ' réponses cochées : note nulle' : pluriel(r.detail.justes, 'bonne réponse', 'bonnes réponses') + ' sur ' + r.detail.attendues}</span>` : ''}
            ${r.detail && r.detail.uncertain ? html`<span class="qcm-verdict-detail">La banque ne donne pas la zone attendue : évalue-toi.</span>` : ''}
            ${saisieOuZone && r.score < 1 ? html`<button type="button" class="btn btn-fantome btn-sm" data-action="qcmSelfCorrect" title="Réponse équivalente autrement formulée (synonyme, orthographe) : compte-la juste">${ico('seal-check', { taille: 16 })}Ma réponse était juste</button>` : ''}
            ${state.qcm.source !== 'static' && !s.annale ? html`<button type="button" class="btn ${s.editing ? 'btn-secondaire' : 'btn-fantome'} btn-sm qcm-corriger" data-action="qcmEditToggle" aria-expanded="${s.editing ? 'true' : 'false'}" title="${i.admin() ? 'La correction edn.fr est fausse ? Corrige-la pour tout le monde.' : 'La correction edn.fr est fausse ? Corrige-la pour toi : elle te suivra sur tous tes appareils.'}">${ico('note-pencil', { taille: 16 })}Corriger ce QCM</button>` : ''}
        </div>
        ${i.isText(q) ? html`<p class="qcm-attendu"><strong>Réponse${i.acceptedAnswers(q).length > 1 ? 's' : ''} acceptée${i.acceptedAnswers(q).length > 1 ? 's' : ''} :</strong> ${i.acceptedAnswers(q).join(' · ')}</p>` : ''}
        <div class="qcm-corr-texte">
            <h3>Correction</h3>
            <div class="qcm-rich">${q.correction_html ? riche(q.correction_html) : (q.correction ? texteEnLignes(q.correction) : html`<p class="qcm-corr-vide">La banque ne joint pas de commentaire de correction à cette question.</p>`)}</div>
            ${images(q, 'correction')}
            ${piecesJointes(q.contenus_correction, q)}
        </div>
        ${s.editMsg && !s.editing ? html`<p class="qcm-corr-message">${ico('check-circle', { taille: 16 })}${s.editMsg}</p>` : ''}
        ${panneauCorrection(q, s, r)}
        ${(q.notions || []).length ? html`<div class="qcm-notions-q">
            <h4>Notions travaillées</h4>
            <div class="qcm-notions-liste">${(q.notions || []).map(function(x) {
                var nn = M && M.notions[i.nrm(x.intitule)];
                return html`<button type="button" class="qcm-notion-lien" data-action="qcmOpenItem" data-args="${attr([String(itemDe(s, q)), partArg, encodeURIComponent(i.nrm(x.intitule)), i.pediaArg()])}" title="${x.chapitre || ''} — voir les questions de cette notion">
                    ${puceRang(x.rang)}<span class="qcm-notion-titre">${x.intitule}</span>${nn && nn.mastery != null ? html`<span class="qcm-notion-pct num">${pct(nn.mastery)} %</span>` : ''}
                </button>`;
            })}</div>
        </div>` : ''}
        ${(q.annales || []).length ? html`<p class="qcm-annales">Annales : ${(q.annales || []).map(function(a) { return typeof a === 'string' ? a : (a.nom || a.libelle || a.annee || ''); }).filter(Boolean).join(', ')}</p>` : ''}
        <div class="qcm-suite">
            <button type="button" class="btn btn-primaire" data-action="qcmNext">${s.idx + 1 >= s.list.length ? 'Voir mon score' : 'Question suivante'}${ico('arrow-right', { taille: 18 })}</button>
        </div>
    </div>`;
}

// ---------------------------------------------------------------- corriger la question (overrides serveur)
function panneauCorrection(q, s, r) {
    if (!s.editing) return '';
    var i = Q(), saisie = i.isText(q), zone = i.isArea(q), orig = q._orig;
    return html`<div id="qcm-edit" class="qcm-edit">
        <h4>Corriger cette question</h4>
        <p class="qcm-edit-portee">${i.admin()
            ? 'Correction partagée : elle s\'applique à tout le monde, tout de suite.'
            : 'Correction personnelle : elle ne s\'applique qu\'à toi, sur tous tes appareils.'} Ta réponse est re-notée aussitôt.</p>
        ${s.editError ? html`<p class="qcm-edit-erreur" role="alert">${ico('warning-circle', { taille: 16 })}${s.editError}</p>` : ''}
        ${!saisie && !zone ? html`<ul class="qcm-edit-props">${i.propsInOrder(q).map(function(p) {
            var o = null; if (orig) orig.props.some(function(x) { if (x.id === p.id) { o = x; return true; } return false; });
            var change = o && (!!o.est_correcte !== !!p.est_correcte || (o.texte || '') !== (p.texte || ''));
            return html`<li class="qcm-edit-prop${change ? ' qcm-edit-prop--change' : ''}">
                <label class="qcm-edit-coche"><input type="checkbox" id="qcm-edit-correct-${p.id}"${p.est_correcte ? html` checked` : ''}><span>${i.isMany(q) ? 'Vraie' : 'Bonne réponse'}</span></label>
                <input type="text" class="saisie" id="qcm-edit-text-${p.id}" value="${p.texte || ''}" aria-label="Texte de la proposition">
                ${change ? html`<span class="qcm-edit-marque">modifiée</span>` : ''}
            </li>`;
        })}</ul>
        <p class="qcm-edit-aide">${i.isMany(q) ? 'Coche exactement les propositions vraies : le barème EDN est recalculé sur ce nouveau corrigé.' : 'Réponse unique : coche la seule bonne réponse.'}</p>` : ''}
        ${saisie ? html`<div class="champ qcm-edit-champ">
            <label for="qcm-edit-accepted">Ajouter des réponses acceptées</label>
            <input type="text" class="saisie" id="qcm-edit-accepted" value="${typeof s.ans === 'string' && r && r.score < 1 ? s.ans : ''}" placeholder="Séparées par un point-virgule">
            <p class="aide">Déjà acceptées : ${i.acceptedAnswers(q).join(' · ') || 'aucune'}</p>
        </div>` : ''}
        ${zone ? html`<p class="qcm-edit-aide">Sur une question à pointer, on ne peut que masquer la question — ou compter ta réponse juste avec le bouton ci-dessus.</p>` : ''}
        <label class="qcm-edit-coche qcm-edit-masquer"><input type="checkbox" id="qcm-edit-hidden"${q._hidden ? html` checked` : ''}><span>Masquer cette question (erronée, hors programme) : elle ne sera plus proposée</span></label>
        <div class="champ qcm-edit-champ">
            <label for="qcm-edit-note">Note pour toi (facultatif)</label>
            <input type="text" class="saisie" id="qcm-edit-note" value="${(q._override && q._override.note) || ''}" placeholder="Pourquoi cette correction">
        </div>
        <div class="qcm-edit-actions">
            <button type="button" class="btn btn-primaire btn-sm" data-action="qcmSaveOverride" data-args="${attr([false])}"${s.editBusy ? html` aria-busy="true" disabled` : ''}>Enregistrer la correction</button>
            <button type="button" class="btn btn-fantome btn-sm" data-action="qcmEditToggle">Annuler</button>
            ${q._override ? html`<button type="button" class="btn btn-fantome btn-sm qcm-edit-reset" data-action="qcmSaveOverride" data-args="${attr([true])}" title="${i.admin() ? 'Supprimer la correction partagée et revenir à la version edn.fr' : 'Supprimer ta correction et revenir à ce que voient les autres'}">Rétablir l'original</button>` : ''}
        </div>
        ${!i.admin() && q._override && q._override.par === 'admin' ? html`<p class="qcm-edit-aide">Cette question porte déjà une correction partagée : enregistrer remplacera, pour toi, ce que tu vois par ta propre version.</p>` : ''}
    </div>`;
}

// ---------------------------------------------------------------- la carte entière
export function carteQuestion(q, s) {
    return html`<article id="qcm-question" class="qcm-q carte">
        ${bandeau(q, s)}
        <div class="qcm-q-corps">
            <div class="qcm-enonce qcm-rich">${q.enonce_html ? riche(q.enonce_html) : texteEnLignes(q.enonce)}</div>
            ${images(q, 'enonce')}
            ${Q().isArea(q) ? '' : piecesJointes(q.contenus_enonce, q)}
            ${zoneReponse(q, s)}
            ${actionsHtml(q, s)}
            ${correctionHtml(q, s)}
        </div>
    </article>`;
}

// ---------------------------------------------------------------- une question déjà passée du dossier en cours
// Dans un dossier progressif, une question donne souvent ce que la suivante suppose connu (un résultat de bilan, le
// diagnostic retenu). En épreuve, on la relit sans sa correction : l'énoncé, ses images, puis, repliées, ses
// propositions avec ce qui a été coché. Lecture seule : ni case ni identifiant de nœud.
export function questionDejaPassee(q, r, rang) {
    var i = Q(), ans = r ? r.ans : null, sans = ans == null || ans === '' || (typeof ans === 'object' && !Object.keys(ans).filter(function(k) { return ans[k]; }).length);
    var reponse;
    if (i.isText(q)) reponse = html`<p class="qcm-revue-donnee"><strong>Ta réponse :</strong> ${sans ? html`<em>sans réponse</em>` : String(ans)}</p>`;
    else if (i.isArea(q)) reponse = '';
    else {
        var props = i.propsInOrder ? i.propsInOrder(q) : (q.propositions || []);
        reponse = html`<details class="trn-prec-props"><summary>Propositions${sans ? ' · sans réponse' : ' et ta réponse'}</summary>
            <ul class="trn-prec-liste">${props.map(function(p) {
                var cochee = !sans && (typeof ans === 'object' ? !!ans[p.id] : ans === p.id);
                return html`<li class="${cochee ? 'trn-prec-cochee' : ''}">${cochee ? html`<span class="trn-prec-marque">${ico('check', { taille: 14 })}<span class="sr-only">Cochée : </span></span>` : ''}<span class="qcm-rich">${riche(p.texte_html || esc(p.texte))}</span></li>`;
            })}</ul></details>`;
    }
    return html`<li class="trn-prec">
        <p class="trn-prec-rang num">Question ${rang}</p>
        <div class="qcm-enonce qcm-rich">${q.enonce_html ? riche(q.enonce_html) : texteEnLignes(q.enonce)}</div>
        ${images(q, 'enonce')}
        ${reponse}
    </li>`;
}

// ---------------------------------------------------------------- la revue corrigée d'une épreuve
// Une question déjà jouée, relue à la fin : l'énoncé, ce qui a été répondu (chaque proposition avec son mot —
// attendue, cochée à tort, manquée), le score et la correction. En lecture seule : aucune case, aucun bouton,
// aucun identifiant de nœud répété — la page en affiche une par question.
export function revueQuestion(q, r, rang) {
    var i = Q(), pseudo = { ans: r ? r.ans : null, revealed: true, results: r ? [r] : [] };
    var sans = !r || r.ans == null || (typeof r.ans === 'object' && !Object.keys(r.ans).length) || r.ans === '';
    var reponse;
    if (i.isText(q)) {
        reponse = html`<p class="qcm-revue-donnee"><strong>Ta réponse :</strong> ${sans ? html`<em>sans réponse</em>` : String(r.ans)}</p>
            <p class="qcm-attendu"><strong>Réponse${i.acceptedAnswers(q).length > 1 ? 's' : ''} acceptée${i.acceptedAnswers(q).length > 1 ? 's' : ''} :</strong> ${i.acceptedAnswers(q).join(' · ')}</p>`;
    } else if (i.isArea(q)) {
        reponse = html`<p class="qcm-revue-donnee">Zone à pointer sur l'image${sans ? html` — <em>sans réponse</em>` : ''}.</p>`;
    } else {
        var props = i.propsInOrder ? i.propsInOrder(q) : (q.propositions || []);
        reponse = html`${sans ? html`<p class="qcm-revue-donnee"><em>Sans réponse</em></p>` : ''}
            <div class="qcm-props qcm-props--revue" role="list" aria-label="Propositions">${props.map(function(p) { return proposition(q, p, pseudo); })}</div>`;
    }
    return html`<article class="qcm-q qcm-q--revue carte${r && r.score < 1 ? ' qcm-q--rate' : ''}" aria-label="Question ${rang}">
        <div class="qcm-revue-tete">
            <span class="qcm-revue-rang num">Question ${rang}</span>
            <span class="qcm-q-type">${i.typeLabel ? i.typeLabel(q) : ''}</span>
            ${r ? puceScore(r.score) : ''}
        </div>
        <div class="qcm-q-corps">
            <div class="qcm-enonce qcm-rich">${q.enonce_html ? riche(q.enonce_html) : texteEnLignes(q.enonce)}</div>
            ${images(q, 'enonce')}
            ${reponse}
            <div class="qcm-corr-texte">
                <h3>Correction</h3>
                <div class="qcm-rich">${q.correction_html ? riche(q.correction_html) : (q.correction ? texteEnLignes(q.correction) : html`<p class="qcm-corr-vide">Pas de commentaire de correction pour cette question.</p>`)}</div>
                ${images(q, 'correction')}
            </div>
        </div>
    </article>`;
}
